import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { CountryDbModel } from '../../../models/country.model';
import { InvoiceAddress } from '../../../models/invoice-address.model';
import { INVOICE_ADDRESS_CONSTANTS } from '../../../constants/invoice-address.constants';
import { splitStreetAndHouseNumber } from '../../../services/invoice-address.util';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { UserState } from '../../../models/state.model';
import { CountryHelperService } from '../../../services/country-helper.service';
import { LoggerService } from '../../../services/logger.service';
import { RefDataService } from '../../../services/ref-data.service';
import { CountrySelectComponent } from '../../../shared/components/country-select/country-select.component';
import { buildPhishingSafeTextRegex } from '../../../shared/directive/app-phising-protection.directive';
import { WidgetUtilService } from '../../../services/widget-util.service';
import { CwIntlTelInputComponent } from '../theme/cw-intl-tel-input/cw-intl-tel-input.component';

/**
 * Standalone "Invoice" payment mode: the checkbox that lets the customer supply a
 * *different* invoice address than their booking contact. Unchecked by default — unchecked =
 * form hidden, nothing sent, the backend snapshots the booking contact. Checked reveals the
 * form, which starts EMPTY (checked means "different address"; prefilling with the booking
 * contact would contradict the choice).
 */
@Component({
  selector: 'app-cw-invoice-address-form',
  templateUrl: './cw-invoice-address-form.component.html',
  styleUrls: ['./cw-invoice-address-form.component.scss'],
  imports: [
    TranslateModule, FormsModule, CountrySelectComponent, CwIntlTelInputComponent,
    CalioMeetingTemplatePipe, TranslationPipe, TrustHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CwInvoiceAddressFormComponent implements OnInit {
  /**
   * Set when the booking form collects no address of its own (`show_address_on_widget = 0`, i.e.
   * `partner.needCustomerAddress` false). There is then nothing for the backend to snapshot, so
   * the address cannot be optional — the checkbox is dropped and the form is always shown.
   */
  readonly isAddressMandatory = input(false);

  readonly lang = input('');

  /** Only for the partner's phishing-guard message, which is a DB template like on the booking form. */
  readonly widgetTemplates = input<CustomBookingMessageTemplate[]>([]);

  /**
   * The partner's own country identifier ("ch", "in", …), used only to preselect the dropdown.
   * The country is a property of the INVOICE address, not of the booker — inheriting the
   * customer's country stamped Swiss addresses as India.
   */
  readonly defaultCountryCode = input('');

  /**
   * Snapshot of the booking form's current values, COPIED into empty fields the moment the
   * invoice form is revealed. Deliberately a plain data object and never a shared control:
   * two controls bound to one value would move together, and the whole point of this form is
   * that the invoice recipient can differ from the booker.
   */
  readonly prefill = input<UserState | null>(null);

  readonly invoiceAddressChangeEvent = output<{ checked: boolean; valid: boolean; value: InvoiceAddress | null }>();

  private readonly refDataService = inject(RefDataService);
  private readonly countryHelperService = inject(CountryHelperService);
  private readonly widgetUtilService = inject(WidgetUtilService);

  protected readonly countries = signal<CountryDbModel[]>([]);
  protected readonly countryId = signal<number | undefined>(undefined);
  /** The countries call came back empty or errored — the dropdown can never offer a choice. */
  protected readonly countriesLoadFailed = signal(false);

  protected readonly checked = signal(false);
  protected readonly companyName = signal('');
  protected readonly prename = signal('');
  protected readonly lastname = signal('');
  protected readonly street = signal('');
  protected readonly zip = signal('');
  protected readonly city = signal('');
  protected readonly email = signal('');
  protected readonly phone = signal('');

  protected readonly touchedFields = signal<Record<string, boolean>>({});

  /** The form is on either because the customer asked for a different address, or because the
   * partner collects none and one is therefore required. */
  protected readonly showForm = computed(() => this.isAddressMandatory() || this.checked());

  protected readonly isEmailValid = computed(() => INVOICE_ADDRESS_CONSTANTS.EMAIL_PATTERN.test(this.email().trim()));

  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  /** The fields the booking form puts the `phishingProtection` directive on. */
  private readonly phishingCheckedFields = computed<Record<string, string>>(() => ({
    prename: this.prename(),
    lastname: this.lastname(),
    company_name: this.companyName(),
    street: this.street(),
    city: this.city(),
  }));

  private readonly phishingUnsafeFields = computed<Record<string, boolean>>(() => {
    const conf = this.widgetUtilService.widgetConf;
    const safeTextRegex = conf?.enable_phishing_protection
      ? buildPhishingSafeTextRegex(conf.phishing_protection_regex)
      : null;
    if (!safeTextRegex) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(this.phishingCheckedFields())
        .map(([field, value]) => [field, !!value.trim() && !safeTextRegex.test(value)])
    );
  });

  protected isFieldPhishing(field: string): boolean {
    return !!this.phishingUnsafeFields()[field];
  }

  protected readonly isFreeTextSafe = computed(
    () => !Object.values(this.phishingUnsafeFields()).some(Boolean)
  );

  protected readonly valid = computed(() => {
    if (!this.showForm()) {
      return true;
    }
    const { street, street_number } = splitStreetAndHouseNumber(this.street());

    return (
      this.prename().trim().length > 0 &&
      this.lastname().trim().length > 0 &&
      // the API requires the house number, so a street without one is not submittable
      street_number.length > 0 &&
      // the backend caps these, and rejects at saveOrFail with no field-level message
      street_number.length <= INVOICE_ADDRESS_CONSTANTS.MAX_STREET_NUMBER_LENGTH &&
      street.length <= INVOICE_ADDRESS_CONSTANTS.MAX_FIELD_LENGTH &&
      this.zip().trim().length > 0 &&
      this.city().trim().length > 0 &&
      // an invoice address without a country cannot be taxed correctly. The backend does allow an
      // absent country_id, so a FAILED country load must not hard-block the booking — but a load
      // still in flight must, or the booking goes out without the country it was about to get.
      (this.countriesLoadFailed() || !!this.countryId()) &&
      this.isEmailValid() &&
      this.isFreeTextSafe()
    );
  });

  constructor() {
    // `isAddressMandatory` is derived from what the booker has typed, so on a page that shows
    // the booking form and the payment card together it flips WHILE this form is on screen.
    // Nothing else emits on that transition — user actions and ngOnInit are the only other
    // emitters — so the parent would keep the stale `{checked: true, valid: false}` and leave
    // Book disabled with no visible cause. A mandatory form is also prefilled here, because it
    // is visible from the moment it becomes mandatory.
    effect(() => {
      const mandatory = this.isAddressMandatory();
      untracked(() => {
        if (mandatory) {
          this.applyPrefill();
        }
        this.emitChange();
      });
    });
  }

  ngOnInit(): void {
    this.loadCountries();
  }

  /** One-time copy of the booking form's values into fields that are still empty. Never
   * overwrites something the customer already typed here. */
  private applyPrefill(): void {
    const source = this.prefill();
    if (!source) {
      return;
    }
    const copy = (target: WritableSignal<string>, value: string | undefined) => {
      if (!target().trim() && value && value.trim()) {
        target.set(value.trim());
      }
    };
    copy(this.companyName, source.company);
    copy(this.prename, source.forename);
    copy(this.lastname, source.lastName);
    copy(this.street, source.street);
    copy(this.zip, source.zip);
    copy(this.city, source.city);
    copy(this.email, source.eMail);
    copy(this.phone, source.phone);
    if (!this.countryId() && source.country_id) {
      this.countryId.set(source.country_id);
    }
  }

  /** Dial code for the phone field: the invoice country when chosen, else the partner country. */
  protected readonly phoneCountryCode = computed(() => {
    const selected = this.countries().find(country => country.id === this.countryId());

    return (selected?.identifier ?? this.defaultCountryCode()).toLowerCase();
  });

  protected onPhoneChange(value: string): void {
    this.phone.set(value ?? '');
    this.onFieldChange();
  }
  protected onCountryChange(selection: CountryDbModel | number | null): void {
    // ng-select emits the bound value when bindValue is set, the whole option otherwise
    const id = typeof selection === 'number' ? selection : selection?.id;
    this.countryId.set(id ?? undefined);
    this.emitChange();
  }

  /** Clearing the form must leave the country preselected again, not blank — otherwise
   * re-opening it silently blocks the booking on a field the customer never touched. */
  private resetCountryToDefault(): void {
    const code = this.defaultCountryCode().toLowerCase();
    const match = code
      ? this.countries().find(country => country.identifier?.toLowerCase() === code)
      : undefined;
    this.countryId.set(match?.id);
  }

  private loadCountries(): void {
    this.refDataService.getCountries().subscribe({
      next: countries => {
        if (!countries?.length) {
          this.countriesLoadFailed.set(true);
          this.emitChange();

          return;
        }
        this.countries.set(this.countryHelperService.findCountriesByDefaultLanguage(countries, this.lang()));

        const code = this.defaultCountryCode().toLowerCase();
        if (code && !this.countryId()) {
          const match = this.countries().find(country => country.identifier?.toLowerCase() === code);
          if (match) {
            this.countryId.set(match.id);
            this.emitChange();
          }
        }
      },
      error: (error: unknown) => {
        // The country is not required by the backend, so a failed load must not leave the book
        // button disabled for every customer on this partner with no visible cause.
        LoggerService.error(error);
        this.countriesLoadFailed.set(true);
        this.emitChange();
      }
    });
  }

  onToggleChecked(): void {
    this.checked.update(checked => !checked);

    if (this.checked()) {
      this.applyPrefill();
    } else {
      this.companyName.set('');
      this.prename.set('');
      this.lastname.set('');
      this.street.set('');
      this.zip.set('');
      this.city.set('');
      this.email.set('');
      this.phone.set('');
      this.touchedFields.set({});
      this.resetCountryToDefault();
    }

    this.emitChange();
  }

  markFieldTouched(field: string): void {
    this.touchedFields.update(touched => ({ ...touched, [field]: true }));
  }

  /**
   * The customer typed a street but no house number, which the booking API requires. Distinct
   * from "street is empty" so the inline message can say which part is missing.
   *
   * Gated on the STREET field's touched flag, not its own: there is no separate number input to
   * blur, so keying it to `street_number` would leave the message permanently hidden and the
   * booking button disabled with no visible cause.
   */
  protected readonly isStreetTouchedAndMissingNumber = computed(() => {
    const street = this.street().trim();

    return !!this.touchedFields()['street']
      && street.length > 0
      && splitStreetAndHouseNumber(street).street_number.length === 0;
  });

  isFieldRequiredAndEmpty(field: string): boolean {
    if (!this.touchedFields()[field]) {
      return false;
    }
    switch (field) {
      case 'prename':
        return this.prename().trim().length === 0;
      case 'lastname':
        return this.lastname().trim().length === 0;
      case 'street':
        return this.street().trim().length === 0;
      case 'zip':
        return this.zip().trim().length === 0;
      case 'city':
        return this.city().trim().length === 0;
      case 'email':
        return this.email().trim().length === 0;
      default:
        return false;
    }
  }

  isEmailInvalidAndFilled(): boolean {
    return this.touchedFields()['email'] && this.email().trim().length > 0 && !this.isEmailValid();
  }

  onFieldChange(): void {
    this.emitChange();
  }

  private emitChange(): void {
    const value: InvoiceAddress | null = this.showForm()
      ? {
          company_name: this.companyName().trim() || undefined,
          prename: this.prename().trim(),
          lastname: this.lastname().trim(),
          // the API keeps the two parts separate, so split the one field we collect
          ...splitStreetAndHouseNumber(this.street()),
          zip: this.zip().trim(),
          city: this.city().trim(),
          country_id: this.countryId(),
          email: this.email().trim(),
          phone: this.phone().trim() || undefined,
        }
      : null;

    this.invoiceAddressChangeEvent.emit({
      checked: this.showForm(),
      valid: this.valid(),
      value,
    });
  }
}
